import React, {useCallback, useEffect, useMemo, useState} from "react";
import {getUserAccountsByUserGroup, updateUserGroup} from "../../../../api/user/UserAccountService";
import {useDispatch, useSelector} from "react-redux";
import {changeRefreshContentStatus} from "../../../../redux/slice/statusSlice";
import {isAdmin} from "../../../../components/commonmethod/CommonMethod";
import {UserType} from "../../../../constant/CodeSpecNum";
import Selector from "../../../../components/selector/Selector";
import {handleActiveToast} from "../../../../components/sonner/ToastBasic";
import {useTranslation} from "react-i18next";
import SimmsInputSearch from "../../../../components/input/SimmsInputSearch";
import DetailLinkedPagination from "../../../../components/detailLayout/DetailLinkedPagination";
import "../../../../components/detailLayout/detailLinkedList.css";
import "../userGroupUsersList.css";

const PAGE_SIZE = 10;
const INDEX_COL_WIDTH = 80;
const MANAGE_COL_WIDTH = 108;

const TABLE_COLUMNS = [
    {label: "순서", field: "_index", width: INDEX_COL_WIDTH},
    {label: "이름", field: "name", minWidth: 100, primary: true},
    {label: "부서", field: "departmentName", minWidth: 96},
    {label: "업체", field: "companyName", minWidth: 96},
    {label: "등록일", field: "createdAt", minWidth: 140},
    {label: "수정일", field: "updatedAt", minWidth: 140},
    {label: "관리", field: "_manage", width: MANAGE_COL_WIDTH, manage: true},
];

function displayValue(value) {
    if (value == null || value === "") {
        return "—";
    }
    return String(value);
}

function buildGridTemplate() {
    const cols = TABLE_COLUMNS.map((col) => {
        if (col.width != null) {
            return `${col.width}px`;
        }
        return `minmax(${col.minWidth}px, 1fr)`;
    }).join(" ");
    return cols;
}

function buildMinWidth() {
    return TABLE_COLUMNS.reduce((sum, col) => sum + (col.width ?? col.minWidth), 0);
}

function mapUserRow(user) {
    return {
        ...user,
        departmentName: user.department?.name,
        companyName: user.company?.name,
    };
}

const UserGroupUsers = ({userGroup}) => {
    const [users, setUsers] = useState([]);
    const [page, setPage] = useState(0);
    const [totalElements, setTotalElements] = useState(0);

    const userAuth = useSelector((state) => state.auth.user);
    const refreshContent = useSelector((state) => state.status.refreshContent);
    const {t} = useTranslation();
    const dispatch = useDispatch();

    const gridTemplate = useMemo(() => buildGridTemplate(), []);
    const minWidth = useMemo(() => buildMinWidth(), []);

    const totalPages = Math.max(1, Math.ceil(totalElements / PAGE_SIZE));

    const pageRows = useMemo(() => {
        const start = page * PAGE_SIZE;
        return users.slice(start, start + PAGE_SIZE).map(mapUserRow);
    }, [users, page]);

    const gridPageable = useMemo(() => ({
        pageNumber: page,
        totalPages,
        totalElements,
        pageSize: PAGE_SIZE,
        size: PAGE_SIZE,
    }), [page, totalPages, totalElements]);

    const loadUsers = useCallback(async () => {
        if (!userGroup?.id) {
            setUsers([]);
            setTotalElements(0);
            setPage(0);
            return;
        }

        await getUserAccountsByUserGroup(userGroup.id, (data) => {
            const nextUsers = [...(data ?? [])];
            setUsers(nextUsers);
            setTotalElements(nextUsers.length);
            setPage((prev) => {
                const maxPage = Math.max(0, Math.ceil(nextUsers.length / PAGE_SIZE) - 1);
                return Math.min(prev, maxPage);
            });
        });
    }, [userGroup?.id]);

    useEffect(() => {
        void loadUsers();
    }, [loadUsers, refreshContent]);

    useEffect(() => {
        setPage(0);
    }, [userGroup?.id]);

    const handleGridPageChange = (nextPageable) => {
        setPage(nextPageable.pageNumber ?? 0);
    };

    const handleUserGroupChange = async (userId, nextGroup) => {
        if (!userId || !nextGroup?.id) {
            return;
        }
        await updateUserGroup({id: userId, userGroupId: nextGroup.id}, () => {
            dispatch(changeRefreshContentStatus());
            handleActiveToast({message: t("updated")});
        });
    };

    const rowOffset = page * PAGE_SIZE;

    return (
        <div className="detail-view-tab-panel user-group-users-list">
            <div className="user-group-users-list__body">
                <div className="user-group-users-list__scroll">
                    {pageRows.length === 0 ? (
                        <div className="user-group-users-list__empty" role="status">
                            등록된 그룹 사용자가 없습니다.
                        </div>
                    ) : (
                        <div className="user-group-users-list__table-wrap">
                            <div
                                className="user-group-users-list__table"
                                style={{minWidth}}
                                role="table"
                            >
                                <div
                                    className="user-group-users-list__thead"
                                    style={{gridTemplateColumns: gridTemplate}}
                                    role="row"
                                >
                                    {TABLE_COLUMNS.map((col) => (
                                        <span
                                            key={col.field}
                                            className={`user-group-users-list__th${
                                                col.primary ? " is-primary" : ""
                                            }${col.field === "_index" ? " is-index" : ""}${
                                                col.manage ? " is-manage" : ""
                                            }`}
                                            role="columnheader"
                                        >
                                            {col.label}
                                        </span>
                                    ))}
                                </div>

                                <div className="user-group-users-list__tbody" role="rowgroup">
                                    {pageRows.map((user, rowIndex) => (
                                        <div
                                            key={user.id}
                                            className="user-group-users-list__tr"
                                            style={{gridTemplateColumns: gridTemplate}}
                                            role="row"
                                        >
                                            {TABLE_COLUMNS.map((col) => {
                                                if (col.field === "_manage") {
                                                    return (
                                                        <span
                                                            key={col.field}
                                                            className="user-group-users-list__td is-manage"
                                                            role="cell"
                                                            onClick={(e) => e.stopPropagation()}
                                                            onKeyDown={(e) => e.stopPropagation()}
                                                        >
                                                            {isAdmin(userAuth) ? (
                                                                <Selector
                                                                    onChange={(value) => handleUserGroupChange(user.id, value)}
                                                                    data={userGroup}
                                                                    uniqueKey={UserType.userGroup}
                                                                    entity="userGroup"
                                                                    auth
                                                                    title="유저그룹"
                                                                >
                                                                    <SimmsInputSearch type="TEXT" placeHolder="그룹명" />
                                                                </Selector>
                                                            ) : (
                                                                "—"
                                                            )}
                                                        </span>
                                                    );
                                                }

                                                const value = col.field === "_index"
                                                    ? rowOffset + rowIndex + 1
                                                    : user[col.field];

                                                return (
                                                    <span
                                                        key={col.field}
                                                        className={`user-group-users-list__td${
                                                            col.primary ? " is-primary" : ""
                                                        }${col.field === "_index" ? " is-index" : ""}`}
                                                        role="cell"
                                                    >
                                                        {displayValue(value)}
                                                    </span>
                                                );
                                            })}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                <div className="user-group-users-list__pagination">
                    <DetailLinkedPagination
                        pageable={gridPageable}
                        onPageChange={handleGridPageChange}
                    />
                </div>
            </div>
        </div>
    );
};

export default UserGroupUsers;
